import { useMutation, useQueryClient } from "@tanstack/react-query";
import { IncidentInterface } from "../../../models/Incident";
import { CACHE_KEY_INCIDENT } from "../../../utils/constants";
import incidentService from "../../../services/incidentService";

interface UpdateIncidentsContext {
  previousIncident: IncidentInterface[];
}

const useUpdateIncident = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    IncidentInterface,
    Error,
    IncidentInterface,
    UpdateIncidentsContext
  >({
    mutationFn: async (incident: IncidentInterface) => {
      // Assuming the ID is stored in the ticket object or can be derived
      const { id, ...incidentData } = incident;
      return incidentService.Update(id || "", incidentData);
    },

    onMutate: async (updatedIncident: IncidentInterface) => {
      await queryClient.cancelQueries({
        queryKey: [CACHE_KEY_INCIDENT, page, pageSize],
      });

      const previousIncident =
        queryClient.getQueryData<IncidentInterface[]>([
          CACHE_KEY_INCIDENT,
          page,
          pageSize,
        ]) || [];

      queryClient.setQueryData<IncidentInterface[]>(
        [CACHE_KEY_INCIDENT, page, pageSize],
        (incidents = []) =>
          incidents.map((incident) =>
            incident.id === updatedIncident.id ? updatedIncident : incident
          )
      );

      return { previousIncident };
    },

    onSuccess: (savedIncident) => {
      queryClient.setQueryData<IncidentInterface[]>(
        [CACHE_KEY_INCIDENT, page, pageSize],
        (incidents) =>
          incidents?.map((incident) =>
            incident.id === savedIncident.id ? savedIncident : incident
          )
      );
    },

    onError: (error, updatedIncident, context) => {
      if (!context) return;

      queryClient.setQueryData<IncidentInterface[]>(
        [CACHE_KEY_INCIDENT, page, pageSize],
        context.previousIncident
      );
    },
  });
};

export default useUpdateIncident;
